import { type WalletClient, type TransactionRequest, encodeFunctionData } from "viem";
import type { TransactionHash } from "@/types";
import { getGatewayContractAddress } from "@/config";
import { TOKEN_IDS } from "@/types";
import { estimateContractTransactionGas, writeContractTransaction } from "@/utils";
import { prepareFormTokenAmountDecimals } from "@/utils/token";
import { gatewayV1Abi } from "@/abi";

// TODO: review whether this is required; this function approves **gateway contract**, no special vault contract
/**
 * Prepares a vault deposit transaction
 */
export const prepareVaultDepositTransaction = async (
    amount: number,
    walletClient: WalletClient,
): Promise<TransactionRequest> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    if (!walletClient.chain?.id) {
        throw new Error("Chain ID is not available");
    }

    const amountBigInt = prepareFormTokenAmountDecimals(amount, TOKEN_IDS.IUSD);
    const vaultAddress = getGatewayContractAddress(walletClient.chain.id);

    return {
        to: vaultAddress,
        data: encodeFunctionData({
            abi: gatewayV1Abi,
            functionName: "stake",
            args: [walletClient.account.address, amountBigInt],
        }),
    };
};

/**
 * Executes a vault deposit transaction
 */
export const gatewayVaultDepositTransaction = async (
    amount: number,
    walletClient: WalletClient,
): Promise<TransactionHash> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    const txRequest = await prepareVaultDepositTransaction(amount, walletClient);
    const hash = await writeContractTransaction(walletClient, txRequest);
    return hash as TransactionHash;
};

/**
 * Estimates gas for a vault deposit transaction
 */
export const estimateVaultDepositTransactionGas = async (
    amount: number,
    walletClient: WalletClient,
): Promise<bigint> => {
    if (!walletClient.account) {
        throw new Error("Wallet account is not available");
    }

    const txRequest = await prepareVaultDepositTransaction(amount, walletClient);
    return estimateContractTransactionGas(walletClient, txRequest);
};
